/* eslint-disable max-len */
/**
 * @typedef {import('BMapsDialog/ScreeningPane/Tabs/ScreeningResults.jsx').ScreeningCompoundInfo} ScreeningCompoundInfo
 */
/* eslint-enable max-len */

import {
    useEffect, useRef, useState, useMemo,
} from 'react';
import {
    Box, Checkbox, FormControlLabel, IconButton, InputLabel, MenuItem, Paper,
    Slider,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import StraightIcon from '@mui/icons-material/Straight';
import ClearIcon from '@mui/icons-material/Clear';
import _ from 'lodash';
import html2canvas from 'html2canvas';
import CoralMap from '@Conifer-Point/interactive-kinase-map/dist/CoralMap';

import { App } from 'BMapsSrc/BMapsApp';
import { IncrementDecrementInput } from 'BMapsUI/common/IncrementDecrementInput';
import { getCompoundLabelColor, getProteinLabelColor } from 'BMapsUI/info_display/InfoDisplay';
import { hslToRgb, hexToHsl } from 'BMapsSrc/util/color_utils';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import Select from 'BMapsUI/common/Select';
import { getTargetInfoMap, setCoralMapState, updateScreeningPropertySettings } from 'BMapsRedux/projectState/access';
import { convertHexToCSS } from 'BMapsUI/ui_utils';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { getNumberedCompoundName } from 'BMapsUI/dialog/ScreeningPane/Tabs/ScreeningResults';
import { Directionality, ScaleTypes, ValueFormatTypes } from 'BMapsSrc/integrations/properties_common';
import { JobTypes, lookupJobDefinition } from 'BMapsSrc/jobs/JobDefinition';
import { dataInfoTooltip } from 'BMapsUI/dialog/ImportPane/ImportCompoundPane/Tabs/JobsResultsTab';
import TooltipInfo from 'BMapsUI/common/TooltipInfo';
import { useSelector } from 'BMapsRedux';
import { DraggableContainer } from 'BMapsUI/common/DraggableContainer';
import { CoralLegend, CoralTooltip } from '../../dialog/FragmentSearch/CoralComponent';
import { getKinaseIdForMapCase } from '../../dialog/FragmentSearch/utils';
import { getPerPoseValue } from '../CompoundTable';
import { ScreeningCoralToolBar, ScoringCoralToolbar } from '../CustomToolbar';
import { defaultPrecision, getFormattedValue } from '../BMapsTable';

const defaultProperty = 'Interaction Score';
const defaultColorScheme = 'kinase';
function getPreferredSort(property, propertyPreferences) {
    const preferPositive = ['Energy Efficiency'];
    return propertyPreferences?.[property]
        ?? preferPositive.includes(property) ? 'positive' : 'negative';
}
function meetsThreshold(value, threshold, preferredSort) {
    if (threshold == null || threshold === '' || Number.isNaN(threshold)) return true;
    const threshAsNum = Number(threshold);
    if (Number.isNaN(threshAsNum)) return true;
    return preferredSort === 'positive' ? value >= threshAsNum : value <= threshAsNum;
}

export function ScoringCoralMap({
    tableData, coralMapState, availableCharts, setChart, tableId,
}) {
    const {
        activeCompounds, property: propInState, colorScheme: schemeInState,
        propertyThresholds, propertyPreferences,
    } = coralMapState;
    const property = propInState || defaultProperty;
    const colorScheme = schemeInState || defaultColorScheme;
    const preferredSort = getPreferredSort(property, propertyPreferences);

    const availableCompounds = tableData.rowItems
        .filter((comp) => getKinaseIdForMapCase(comp.caseData?.mapCase) !== null);
    const propertyThreshold = propertyThresholds[property];

    const compounds = [];
    activeCompounds.forEach((structID) => {
        const compound = App.Workspace.getLoadedCompounds()
            .find((c) => c.getUniqueStructId() === structID);
        if (compound) {
            compounds.push(compound);
        } else {
            // compound was deleted from bmaps so remove from redux
            setCoralMapState({
                activeCompounds: activeCompounds.filter(
                    (comp) => comp !== structID
                ),
            }, tableId);
        }
    });

    const coralRef = useRef(null);

    const styles = {};

    const data = {};

    const legendStyles = compounds
        .map((comp) => ({
            compound: comp.resSpec,
            color: convertHexToCSS(getCompoundLabelColor(comp)) || 'red',
        }));

    // used for linterp adjust.
    let minValue = null;
    let maxValue = null;

    compounds.forEach((activeCompound) => {
        const poses = App.Workspace.getLoadedCompounds()
            .filter((c) => (
                c.getUniqueStructId() === activeCompound?.getUniqueStructId()
                && typeof getPerPoseValue(c, property, c.caseData?.mapCase) === 'number'
            ));
        for (const pose of poses) {
            const kinaseId = getKinaseIdForMapCase(pose.caseData.mapCase);
            const propertyValue = getPerPoseValue(pose, property, pose.caseData.mapCase);
            let baseColor = 1;
            if (colorScheme === 'compound') {
                const color = getCompoundLabelColor(activeCompound);
                const hslColor = hexToHsl(color);
                baseColor = hslColor[0];
            }
            if (colorScheme === 'kinase') {
                const color = getProteinLabelColor(pose.caseData);
                const hslColor = hexToHsl(color);
                baseColor = hslColor[0];
            }
            if (colorScheme === 'target') {
                const targetMap = getTargetInfoMap();
                const { isTarget } = targetMap.get(pose.caseData.mapCase);
                if (isTarget) {
                    baseColor = 245;
                }
            }

            if (!meetsThreshold(propertyValue, propertyThreshold, preferredSort)) {
                continue;
            }

            if (maxValue == null || maxValue < propertyValue) maxValue = propertyValue;
            if (minValue == null || minValue > propertyValue) minValue = propertyValue;
            if (data[kinaseId]) {
                const storedEntry = data[kinaseId].compounds.get(activeCompound);
                if (!storedEntry) {
                    data[kinaseId].compounds.set(activeCompound, {
                        propertyValue,
                        baseColor,
                        name: pose.resSpec,
                    });
                } else if (
                    (propertyValue > storedEntry.propertyValue && preferredSort === 'positive')
                    || (propertyValue < storedEntry.propertyValue && preferredSort === 'negative')) {
                    data[kinaseId].compounds.set(activeCompound, {
                        propertyValue,
                        baseColor,
                        name: pose.resSpec,
                    });
                }
            } else {
                data[kinaseId] = {
                    compounds: new Map([[activeCompound, {
                        propertyValue,
                        baseColor,
                        name: pose.resSpec,
                    }]]),
                };
                const color = hslToRgb(baseColor, 1, 0.45);
                legendStyles.push({
                    kinase: `${kinaseId} - (${pose.caseData.mapCase.pdbID})`,
                    color: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
                });
            }
        }
    });
    // data is now {kinase:{compounds:{compound => value}}
    const filteredCompounds = compounds.filter((comp) => (
        !Object.values(data).some((perKinase) => perKinase.compounds.has(comp))
    ));

    // now adjust colors based off property value min and max and basecolor
    // and add to styles
    for (const [kinase, comp] of Object.entries(data)) {
        const colors = [];
        const toolTips = [];
        let nodeSize = 4;

        // compound will probably be used when I get around to tooltips
        for (const [compound, colorData] of comp.compounds) {
            const h = colorData.baseColor;
            const s = 1;

            // lightness linear interp adjustment
            let y0 = 0.8;
            let y1 = 0.45;

            let x0 = minValue;
            let x1 = maxValue;
            if (preferredSort === 'negative') {
                x0 = maxValue;
                x1 = minValue;
            }
            const x = colorData.propertyValue;
            let l = 0.5;
            if (x1 !== x0) {
                l = (y0 * (x1 - x) + y1 * (x - x0)) / (x1 - x0);
            }
            const valueAdjustedColor = hslToRgb(h, s, l);
            colors.push(valueAdjustedColor);
            toolTips.push({
                name: colorData.name,
                value: colorData.propertyValue.toFixed(defaultPrecision),
            });

            // nodeSize linear interp adjustment
            y0 = 9 - (Object.keys(data).length - 1);
            y1 = 13 + (Object.keys(data).length - 1);

            if (y0 <= 4) y0 = 4;
            if (y1 >= 20) y1 = 20;
            if (x1 !== x0) {
                const newNodeSize = (y0 * (x1 - x) + y1 * (x - x0)) / (x1 - x0);
                if (newNodeSize > nodeSize) {
                    nodeSize = newNodeSize;
                }
            }
        }
        // average adjusted colors for all compounds
        const total = [0, 0, 0];
        colors.forEach((color) => {
            color.forEach((val, index) => {
                total[index] += val;
            });
        });
        const finalColor = total.map((val) => (val / colors.length));

        // add to styles
        styles[kinase] = {
            nodeColor: `rgb(${finalColor[0]}, ${finalColor[1]}, ${finalColor[2]})`,
            branchColor: `rgb(${finalColor[0]}, ${finalColor[1]}, ${finalColor[2]})`,
            nodeSize,
            tooltip:
    <>
        <p>{`${property} for ${kinase}`}</p>
        {toolTips.map((toolTip) => (
            <div
                key={toolTip.name}
                style={{
                    display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '.5rem 0',
                }}
            >
                <p>{toolTip.name}</p>
                <p>{toolTip.value}</p>
            </div>
        ))}
    </>,
        };
    }

    const properties = _.uniqBy(tableData.allPerPoseCols, (column) => column.title)
        .filter((column) => column.key !== 'Best Pose Name');

    const exportOptions = [
        {
            title: 'Export to PNG',
            handleExport: () => {
                html2canvas(coralRef.current).then((canvas) => {
                    const imgLink = canvas.toDataURL('image/png');
                    const anchor = document.createElement('a');
                    anchor.setAttribute('href', imgLink);
                    anchor.setAttribute('download', `${property}.png`);
                    anchor.click();
                    anchor.remove();
                });
            },
        },
    ];

    const addCompound = (compound) => {
        if (activeCompounds.includes(compound.getUniqueStructId())) return;
        setCoralMapState(
            { activeCompounds: [...activeCompounds, compound.getUniqueStructId()] }, tableId
        );
    };

    const removeCompound = (compound) => {
        if (!activeCompounds.includes(compound.getUniqueStructId())) return;
        setCoralMapState({
            activeCompounds: activeCompounds.filter(
                (comp) => comp !== compound.getUniqueStructId()
            ),
        }, tableId);
    };

    const changeProperty = (prop) => {
        setCoralMapState({ property: prop }, tableId);
    };

    const setColorScheme = (scheme) => {
        setCoralMapState({ colorScheme: scheme }, tableId);
    };

    let defaultThresh = null;
    if (preferredSort === 'negative' && maxValue != null) defaultThresh = Math.ceil(maxValue);
    if (preferredSort === 'positive' && minValue != null) defaultThresh = Math.floor(minValue);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <ScoringCoralToolbar toolBarId="CoralMap" charts={availableCharts} setChart={setChart} compounds={availableCompounds} addCompound={addCompound} exportOptions={exportOptions} />
            <Box
                sx={{
                    border: '1px solid lightgray', margin: '.5rem 0', flexDirection: 'column', display: 'flex', overflow: 'hidden',
                }}
                ref={coralRef}
            >
                <CompoundPropertyTable
                    activeCompounds={compounds}
                    property={property}
                    properties={properties}
                    setProperty={changeProperty}
                    remove={removeCompound}
                    colorScheme={colorScheme}
                    setColorScheme={setColorScheme}
                    filteredCompounds={filteredCompounds}
                />
                <Box sx={{
                    height: '25rem', display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}
                >
                    <Box sx={{ height: '100%', width: '100%' }}>
                        <CoralMap
                            TooltipComponent={CoralTooltip}
                            styles={{ ...styles, branchColor: 'lightgray', label: { textColor: 'black' } }}
                        />
                    </Box>
                </Box>
                <CoralLegend colorData={{
                    legendStyles, colorScheme,
                }}
                />
            </Box>
            <Typography component="div" style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Threshold
                { ` (inclusive ${preferredSort === 'positive' ? 'min.' : 'max.'})` }

            </Typography>
            <IncrementDecrementInput
                name="Threshold"
                value={propertyThreshold ?? defaultThresh ?? ''}
                setValue={(value) => setCoralMapState(
                    {
                        propertyThresholds:
                        { ...propertyThresholds, [property]: value },
                    }, tableId
                )}
                fallbackValue={defaultThresh}
            />

        </Box>
    );
}

function CompoundPropertyTable({
    activeCompounds, remove, colorScheme, setColorScheme,
    property, setProperty, properties,
    filteredCompounds,
}) {
    const colorSchemeOptions = [
        {
            id: 'compound',
            label: 'Compound',
        },
        {
            id: 'kinase',
            label: 'Kinase',
        },
        {
            id: 'target',
            label: 'Target / Off-Target',
        },
    ];

    return (
        <TableContainer component={Paper} sx={{ flexGrow: 1, marginBottom: '.5rem', minHeight: '5rem' }}>
            <Table aria-label="Active Compounds table" size="small">
                <TableHead>
                    <TableRow>
                        <TableCell align="left">
                            <Select
                                value={property}
                                label="Property"
                                onChange={(e) => setProperty(e.target.value)}
                                size="small"
                                slotProps={{ select: { native: false } }}
                            >
                                {properties.map((option) => (
                                    <MenuItem
                                        key={option.key}
                                        value={option.key}
                                    >
                                        {option.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </TableCell>
                        <TableCell align="right">
                            <Select
                                value={colorScheme}
                                label="Color Scheme"
                                onChange={(e) => setColorScheme(e.target.value)}
                                size="small"
                                slotProps={{ select: { native: false } }}
                            >
                                {colorSchemeOptions.map((option) => (
                                    <MenuItem
                                        key={option.id}
                                        value={option.id}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </TableCell>
                    </TableRow>
                </TableHead>
                {!!activeCompounds && activeCompounds.length > 0 && (
                <TableBody>
                    {activeCompounds.map((compound) => (
                        <CompoundRow
                            key={compound.resSpec}
                            name={compound.resSpec}
                            remove={() => remove(compound)}
                            isFiltered={filteredCompounds.includes(compound)}
                        />
                    ))}
                </TableBody>
                )}

            </Table>
        </TableContainer>
    );
}

function CompoundRow({
    name, remove, isFiltered,
}) {
    return (
        <TableRow
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
        >
            <TableCell align="left">
                {name}
            </TableCell>
            <TableCell align="left">
                {!!isFiltered && '(filtered)'}
            </TableCell>
            <TableCell align="right">
                <BMapsTooltip title="Remove compound from coral map">
                    <IconButton onClick={remove}>
                        <ClearIcon />
                    </IconButton>
                </BMapsTooltip>
            </TableCell>
        </TableRow>
    );
}

/**
 *
 * @param {{
 * activeScreeningCompoundInfoList: ScreeningCompoundInfo[],
 * availableScreeningCompoundInfoList: ScreeningCompoundInfo[],
 * availableCharts: string[], setChart: (chart: string) => void}} param0
 * @returns
 */
export function ScreeningCoralMap({
    activeScreeningCompoundInfoList, availableScreeningCompoundInfoList,
    updateScreeningCompoundInfo,
    availableCharts, setChart,
}) {
    const [staticMap, setStaticMap] = useState(false);
    const [legendSize, setLegendSize] = useState(1);
    const [svgZoom, setSvgZoom] = useState(1);

    const { activeProperty, propertySettings } = useSelector((state) => (
        state.projectState.tableState.screeningTable.chartState.coralMap
    ));
    const setActiveProperty = (property) => {
        setCoralMapState({ activeProperty: property }, 'screeningTable');
    };
    const [localPropertySettings, setLocalPropertySettings] = useState(propertySettings);

    const availableProperties = getAllAvailableProperties(availableScreeningCompoundInfoList);
    const propertyLabel = useMemo(
        () => screeningPropInfo(activeProperty)?.label || activeProperty,
        [activeProperty]
    );

    const minMaxThresholdSlider = useMemo(
        () => getMinMaxThresholdSlider(activeScreeningCompoundInfoList, activeProperty, true),
        [activeScreeningCompoundInfoList, activeProperty]
    );
    const coralRef = useRef(null);

    function getDefaultActiveProperty(availableProps) {
        const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo || {};
        for (const [key, { defaultActive }] of Object.entries(propInfos)) {
            if (defaultActive && availableProps.includes(key)) {
                return key;
            }
        }
        return availableProps[0];
    }

    useEffect(() => {
        const property = activeProperty;
        if (!property || !availableProperties.includes(property)) {
            setActiveProperty(getDefaultActiveProperty(availableProperties));
        }
        if (!localPropertySettings || !localPropertySettings[property]) {
            setLocalPropertySettings({
                ...propertySettings,
                [property]: defaultSettings(property),
            });
        }
    }, [availableProperties]);

    // if adding or removing a compound and user hasn't set property settings, auto update
    useEffect(() => {
        if (!propertySettings || !propertySettings[activeProperty]) {
            setLocalPropertySettings({
                ...propertySettings,
                [activeProperty]: defaultSettings(activeProperty),
            });
        }
    }, [activeScreeningCompoundInfoList]);

    function defaultSettings(property) {
        const propInfo = screeningPropInfo(property) || {};
        return {
            scale: propInfo.defaultScale || ScaleTypes.LINEAR,
            nodeSizeValues: [2, 16],
            propertyThresholds: getDefaultThresholds(
                activeScreeningCompoundInfoList, property, true
            ),
            colorIntensity: [0.3, 0.9],
            roundedLegend: getDefaultValueFormatting(property),
        };
    }
    /**
     * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
     * @param {string} property
     * @param {Boolean} allowDefaults
     * @returns {[number?, number?]}
     *
     * @description Returns a min and max value that include all values for a given property
     * as well as the default thresholds for that property. This function is similar to
     * getDefaultThresholds, but it is used for the minimum and maximum values possible on
     * the slider instead of just the default slider values.
     */
    function getMinMaxThresholdSlider(screeningCompoundInfoList, property, allowDefaults) {
        let absoluteMin = null;
        let absoluteMax = null;
        if (allowDefaults) {
            const defaults = screeningPropInfo(property)?.defaultThresholds || {};
            if (defaults.min != null) {
                absoluteMin = defaults.min;
                if (defaults.max == null) absoluteMax = absoluteMin;
            }
            if (defaults.max != null) {
                absoluteMax = defaults.max;
                if (defaults.min == null) absoluteMin = absoluteMax;
            }
        }
        for (const screeningCompoundInfo of screeningCompoundInfoList) {
            if (!screeningCompoundInfo) continue;
            const min = screeningCompoundInfo.getMinValue(property);
            const max = screeningCompoundInfo.getMaxValue(property);
            if ((absoluteMin == null || absoluteMin > min) && min != null) absoluteMin = min;
            if ((absoluteMax == null || absoluteMax < max) && max != null) absoluteMax = max;
        }
        return [absoluteMin, absoluteMax];
    }

    /**
     * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
     * @param {string} property
     * @param {Boolean} allowDefaults
     * @returns {[number?, number?]}
     *
     * @description Returns a min and max value based on the default property information.
     * This minimum and maximum doesn't necessarily include all values for a given property.
     * This function is similar to getMinMaxThresholdSlider, but it is used for the default
     * slider values instead of the minimum and maximum values possible on the slider.
     */
    function getDefaultThresholds(screeningCompoundInfoList, property, allowDefaults) {
        let absoluteMin = null;
        let absoluteMax = null;
        for (const screeningCompoundInfo of screeningCompoundInfoList) {
            if (!screeningCompoundInfo) continue;
            const min = screeningCompoundInfo.getMinValue(property);
            const max = screeningCompoundInfo.getMaxValue(property);
            if ((absoluteMin == null || absoluteMin > min) && min != null) absoluteMin = min;
            if ((absoluteMax == null || absoluteMax < max) && max != null) absoluteMax = max;
        }
        if (allowDefaults) {
            const defaults = screeningPropInfo(property)?.defaultThresholds || {};
            if (defaults.min != null) {
                absoluteMin = defaults.min;
                if (absoluteMin > absoluteMax) absoluteMax = absoluteMin;
            }
            if (defaults.max != null) {
                absoluteMax = defaults.max;
                if (absoluteMax < absoluteMin) absoluteMin = absoluteMax;
            }
        }
        return [absoluteMin, absoluteMax];
    }

    function changeProperty(prop) {
        setActiveProperty(prop);
        if (!localPropertySettings[prop]) {
            setLocalPropertySettings({
                ...propertySettings,
                [prop]: defaultSettings(prop),
            });
        }
    }

    const propertyPreference = getScreeningPropertySort(activeProperty);

    const styles = calculateCoralMapStyles({
        activeProperty,
        activeScreeningCompoundInfoList,
        propertyPreference,
        availableScreeningCompoundInfoList,
        propertyLabel,
        propertySettings: localPropertySettings[activeProperty],
    });

    const exportOptions = [
        {
            title: 'Export to PNG',
            handleExport: async () => {
                setStaticMap(true);

                // wait for the static map to be set
                await new Promise((resolve) => requestAnimationFrame(() => resolve()));

                html2canvas(coralRef.current).then((canvas) => {
                    const imgLink = canvas.toDataURL('image/png');
                    const anchor = document.createElement('a');
                    anchor.setAttribute('href', imgLink);
                    anchor.setAttribute('download', `${activeProperty}.png`);
                    anchor.click();
                    anchor.remove();
                });
                setStaticMap(false);
            },
        },
    ];

    function updatePropertySettings(newSettings, previousSettings) {
        // update our redux property settings
        updateScreeningPropertySettings(newSettings);
        // update our local property settings
        setLocalPropertySettings({
            ...previousSettings,
            ...newSettings,
        });
    }
    if (!activeProperty) return null;
    if (!localPropertySettings[activeProperty]) return null;

    return (
        <>
            <ScreeningCoralToolBar
                toolBarId="CoralMap"
                charts={availableCharts}
                setChart={setChart}
                exportOptions={exportOptions}
                screeningCompoundInfoList={availableScreeningCompoundInfoList}
                updateScreeningCompoundInfo={updateScreeningCompoundInfo}
            />
            <Paper
                style={{
                    display: 'flex',
                    overflow: 'hidden',
                    height: '100%',
                }}
            >
                <AdvancedStyleOptions
                    propertySettings={localPropertySettings}
                    setPropertySettings={(newSettings) => {
                        updatePropertySettings(newSettings, localPropertySettings);
                    }}
                    minMaxThresholdSlider={minMaxThresholdSlider}
                    activeProperty={activeProperty}
                    availableProperties={availableProperties}
                    changeProperty={changeProperty}
                    setLegendSize={setLegendSize}
                    legendSize={legendSize}
                />
                <div
                    style={{
                        height: '100%',
                        flex: 1,
                        position: 'relative',
                        overflow: 'hidden',

                    }}
                    ref={coralRef}
                >
                    <CoralMap
                        onZoomChange={(zoomLevel) => {
                            setSvgZoom(zoomLevel);
                        }}
                        TooltipComponent={CoralTooltip}
                        styles={{ ...styles }}
                        staticMap={
                                        staticMap
                                        // set to true when exporting
                                        // so we can take a picture without tooltip
                                    }
                    />
                    <DraggableContainer initial="bottomLeft" scale={legendSize}>
                        <DialogContentSection
                            titleVariant="subtitle1"
                            title="Legend"
                            disablePadding
                        >
                            <Box sx={{
                                display: 'flex', flexDirection: 'row', gap: '1rem', whiteSpace: 'nowrap',
                            }}
                            >
                                {activeScreeningCompoundInfoList.map((screeningCompoundInfo) => (
                                    <div style={{ display: 'flex', flexDirection: 'column' }} key={screeningCompoundInfo.smiles}>
                                        <ScreeningCoralLegend
                                            activePropertySettings={
                                                        localPropertySettings[activeProperty]
                                                    }
                                            propertyLabel={propertyLabel}
                                            screeningCompoundInfo={screeningCompoundInfo}
                                            propertyPreference={propertyPreference}
                                            activeProperty={activeProperty}
                                            nodeScaleFactor={(1/legendSize) * svgZoom}
                                        />
                                        <Typography>
                                            {
                                            getNumberedCompoundName(
                                                screeningCompoundInfo,
                                                availableScreeningCompoundInfoList
                                            )
                                            }
                                        </Typography>
                                    </div>
                                ))}
                            </Box>
                        </DialogContentSection>
                    </DraggableContainer>
                </div>
            </Paper>
        </>
    );
}

function AdvancedStyleOptions({
    minMaxThresholdSlider,
    propertySettings,
    setPropertySettings,
    activeProperty,
    availableProperties,
    changeProperty,
    setLegendSize,
    legendSize,
}) {
    const activePropSettings = propertySettings[activeProperty];
    const propInfo = screeningPropInfo(activeProperty) || {};
    const lightnessMin = 0.2;
    const lightnessMax = 1;

    const handleNodeSliderChange = (event, newValue) => {
        setPropertySettings({
            ...propertySettings,
            [activeProperty]: {
                ...activePropSettings,
                nodeSizeValues: newValue,
            },
        });
    };

    const handleThresholdSliderChange = (event, newValue) => {
        setPropertySettings({
            ...propertySettings,
            [activeProperty]: {
                ...activePropSettings,
                propertyThresholds: newValue,
            },
        });
    };
    const handleColorIntensityChange = (event, newValue) => {
        const val = invertColor(newValue);
        setPropertySettings({
            ...propertySettings,
            [activeProperty]: {
                ...activePropSettings,
                colorIntensity: val,
            },
        });
    };

    const handleLegendSizeChange = (event, newValue) => {
        setLegendSize(newValue);
    };

    function invertColor([a, b], min=lightnessMin, max=lightnessMax) {
        return [min + max - b, min + max - a];
    }
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid lightgray',
            padding: '0 .5rem',
            overflowY: 'auto',
            overflowX: 'visible',
        }}
        >
            <DialogContentSection
                title="Visualization Options"
                titleVariant="subtitle1"
                disablePadding
                TitleProps={{ style: { padding: '0 .5rem' } }}
                contentStyle={{ overflow: 'visible' }}
            >
                <div style={{ padding: '0.5rem 1rem 0 1rem' }}>
                    <PropertyControl
                        properties={availableProperties}
                        activeProperty={activeProperty}
                        setActiveProperty={changeProperty}
                    />
                    <Select
                        value={activePropSettings.scale}
                        label="Scale"
                        onChange={(e) => {
                            setPropertySettings({
                                ...propertySettings,
                                [activeProperty]: {
                                    ...activePropSettings,
                                    scale: e.target.value,
                                },
                            });
                        }}
                        size="small"
                        slotProps={{ select: { native: false } }}
                        sx={{ margin: '.5rem 0' }}
                    >
                        <MenuItem
                            key="Linear"
                            value={ScaleTypes.LINEAR}
                        >
                            Linear
                        </MenuItem>
                        <MenuItem
                            key="Logarithmic"
                            value={ScaleTypes.LOGARITHMIC}
                        >
                            Logarithmic
                        </MenuItem>
                    </Select>
                    {minMaxThresholdSlider[0] !== minMaxThresholdSlider[1] && (
                        <>
                            <InputLabel>Property Threshold</InputLabel>
                            <FlexibleSlider
                                value={activePropSettings.propertyThresholds}
                                onChange={handleThresholdSliderChange}
                                valueLabelDisplay="auto"
                                min={minMaxThresholdSlider[0]}
                                max={minMaxThresholdSlider[1]}
                                logarithmic={activePropSettings.scale === ScaleTypes.LOGARITHMIC}
                                unit={propInfo.unit}
                                precision={propInfo.precision}
                                slots={{ valueLabel: SliderTooltip }}
                            />
                        </>
                    )}
                    <InputLabel>Node Size Range</InputLabel>
                    <Slider
                        value={activePropSettings.nodeSizeValues}
                        onChange={handleNodeSliderChange}
                        valueLabelDisplay="auto"
                        min={1}
                        max={25}
                        step={0.5}
                        slots={{ valueLabel: SliderTooltip }}
                    />
                    <InputLabel>Color Range</InputLabel>
                    <Slider
                        value={invertColor(activePropSettings.colorIntensity)}
                        onChange={handleColorIntensityChange}
                        valueLabelDisplay="auto"
                        min={lightnessMin}
                        max={lightnessMax}
                        step={0.01}
                        valueLabelFormat={(value) => value.toFixed(2)}
                        slots={{ valueLabel: SliderTooltip }}
                    />
                </div>
            </DialogContentSection>
            <DialogContentSection
                title="Legend Controls"
                titleVariant="subtitle1"
                disablePadding
                TitleProps={{ style: { padding: '0 .5rem' } }}
                contentStyle={{ overflow: 'visible' }}
            >
                <div style={{ padding: '0 1rem' }}>
                    <InputLabel>Legend Size</InputLabel>
                    <Slider
                        value={legendSize}
                        onChange={handleLegendSizeChange}
                        valueLabelDisplay="auto"
                        min={0.1}
                        max={1.5}
                        step={0.01}
                        valueLabelFormat={(value) => `${value.toFixed(2)}x`}
                        slots={{ valueLabel: SliderTooltip }}
                    />
                    <Select
                        value={activePropSettings.roundedLegend}
                        label="Value Precision"
                        onChange={(e) => {
                            setPropertySettings({
                                ...propertySettings,
                                [activeProperty]: {
                                    ...activePropSettings,
                                    roundedLegend: e.target.value,
                                },
                            });
                        }}
                        size="small"
                        slotProps={{ select: { native: false } }}
                        sx={{ margin: '.5rem 0' }}
                    >
                        <MenuItem
                            key="WholeNumber"
                            value={ValueFormatTypes.WHOLE_NUMBER}
                        >
                            1.0 (whole numbers)
                        </MenuItem>
                        <MenuItem
                            key="Tenths"
                            value={ValueFormatTypes.TENTHS}
                        >
                            0.1 (tenths)
                        </MenuItem>
                        <MenuItem
                            key="Hundredths"
                            value={ValueFormatTypes.HUNDREDTHS}
                        >
                            0.01 (hundredths)
                        </MenuItem>
                        <MenuItem
                            key="Thousandths"
                            value={ValueFormatTypes.THOUSANDTHS}
                        >
                            0.001 (thousandths)
                        </MenuItem>
                        <MenuItem
                            key="Decade"
                            value={ValueFormatTypes.DECADE}
                        >
                            Powers of 10
                        </MenuItem>
                    </Select>
                </div>
            </DialogContentSection>
            <DialogContentSection
                title="Tips"
                titleVariant="subtitle1"
                disablePadding
                TitleProps={{ style: { padding: '0 .5rem' } }}
                contentStyle={{ overflow: 'visible' }}
            >
                <ul
                    style={{
                        fontStyle: 'italic',
                        fontSize: 'smaller',
                        paddingLeft: '1.2em',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        maxWidth: '20em',
                    }}
                >
                    <li>Drag the Legend box to reposition it</li>
                    <li>Edit cmpds from the Compounds menu</li>
                    <li>Use resize handle to enlarge coral map</li>
                </ul>
            </DialogContentSection>
        </Box>
    );
}

function SliderTooltip({ children, open, value }) {
    return (
        <BMapsTooltip
            open={open}
            title={value}
            placement="top"
            slotProps={{
                popper: {
                    modifiers: [ // removes lag with tooltip
                        { name: 'eventListeners', options: { scroll: true, resize: true } },
                    ],
                },
            }}
        >
            {children}
        </BMapsTooltip>
    );
}

function PropertyControl({
    properties: propsIn, activeProperty, setActiveProperty,
}) {
    const [showAdvancedProps, setShowAdvancedProps] = useState(false);
    if (propsIn?.length <= 1) {
        return null;
    }
    const propInfos = lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    const properties = propsIn.filter((prop) => showAdvancedProps || !propInfos[prop]?.advanced);
    return (
        <div>
            <Select
                value={activeProperty}
                label="Property"
                onChange={(e) => { setActiveProperty(e.target.value); }}
                size="small"
                slotProps={{
                    select: {
                        native: false,
                        // Don't show info tooltip icon for selected value
                        // (only when picklist is open)
                        renderValue: (selected) => (propInfos[selected]?.label || selected),
                    },
                }}
            >
                {properties.map((option) => (
                    <MenuItem
                        key={option}
                        value={option}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <span>{propInfos[option]?.label || option}</span>
                            <div style={{ padding: '.2rem 0 0 0' }}>
                                <TooltipInfo
                                    title={dataInfoTooltip(propInfos[option])}
                                    placement="right"
                                    noMaxWidth
                                    enterNextDelay={100}
                                    fontSize="inherit"
                                />
                            </div>
                        </div>
                    </MenuItem>
                ))}
            </Select>
            <FormControlLabel
                sx={{ display: 'block', marginLeft: '1em' }}
                control={(
                    <Checkbox
                        checked={showAdvancedProps}
                        onChange={(e) => setShowAdvancedProps(e.target.checked)}
                    />
                )}
                label="Include Advanced Props"
            />
        </div>
    );
}

function ScreeningCoralLegend({
    propertyLabel, screeningCompoundInfo,
    propertyPreference, activeProperty,
    activePropertySettings,
    nodeScaleFactor,
}) {
    if (!activePropertySettings) {
        return null;
    }
    const {
        scale, nodeSizeValues, propertyThresholds, roundedLegend,
        colorIntensity,
    } = activePropertySettings;
    const useSignedLog = scale === ScaleTypes.LOGARITHMIC;
    const h = screeningCompoundInfo.getBaseHue();
    const s = 1;
    const minValue = propertyThresholds[0];
    const maxValue = propertyThresholds[1];
    const percentages = [0, 0.25, 0.5, 0.75, 1];
    const valueList = [];
    if (scale === ScaleTypes.LOGARITHMIC) {
        const x0 = signedLog(minValue);
        const x1 = signedLog(maxValue);
        for (const percentage of percentages) {
            const logValue = percentage * (x1 - x0) + x0;
            const value = signedLogInverse(logValue);
            const roundedValue = roundValue(value, roundedLegend);
            if (valueList.includes(roundedValue)) {
                continue;
            }
            valueList.push(roundedValue);
        }
    } else {
        for (const percentage of percentages) {
            const value = percentage * (maxValue - minValue) + minValue;
            const roundedValue = roundValue(value, roundedLegend);
            if (valueList.includes(roundedValue)) {
                continue;
            }
            valueList.push(roundedValue);
        }
    }
    const styleValues = [];
    let offSet = 0;
    let maxRadius = 0;
    for (let i = 0; i < valueList.length; i++) {
        const value = valueList[i];
        let r = calculateInterpolatedValue(
            value, minValue, maxValue,
            nodeSizeValues[0], nodeSizeValues[1],
            {
                useSignedLog,
                propertyPreference,
            },
        ) * nodeScaleFactor;
        if (r < 0) {
            r = 0;
        }
        maxRadius = Math.max(maxRadius, r);
        let l = calculateInterpolatedValue(
            value, minValue, maxValue,
            colorIntensity[1], colorIntensity[0],
            {
                useSignedLog,
                propertyPreference,
            },
        );
        if (l < 0) {
            l = 0;
        }
        if (l > 1) {
            l = 1;
        }
        const finalColor = hslToRgb(h, s, l);
        offSet = offSet + r + 5;
        styleValues.push({
            r, color: `rgb(${finalColor[0]}, ${finalColor[1]}, ${finalColor[2]})`, value, offset: offSet, percentage: percentages[i],
        });
        offSet += r;
    }
    const propInfo = screeningPropInfo(activeProperty);
    const units = propInfo?.unit;

    styleValues.sort((a, b) => a.r - b.r);
    return (
        <Typography component="div">
            <div style={{ display: 'flex' }}>
                <BMapsTooltip
                    title={dataInfoTooltip(propInfo)}
                    placement="right"
                    noMaxWidth
                >
                    {propertyLabel}
                </BMapsTooltip>
                { propertyPreference && (
                    <BMapsTooltip
                        title={propertyPreference === 'positive' ? 'Higher is better' : 'Lower is better'}
                        placement="right"
                    >
                        <Box
                            sx={{
                                transform: propertyPreference === 'negative' ? 'rotate(180deg)' : 'none',
                                display: 'flex',
                            }}
                        >
                            <StraightIcon sx={{ alignSelf: 'center', fontSize: 'inherit' }} />
                        </Box>
                    </BMapsTooltip>
                )}
            </div>
            <table>
                <tbody>
                    {styleValues.map(({
                        r, color, value, offset, percentage,
                    }, i) => (
                        <tr style={{ fontSize: 'small', fontWeight: 'bold' }} key={`legend-${percentage}`}>
                            <td>
                                <svg width={2 * maxRadius + 5} height={2 * r + 5} xmlns="http://www.w3.org/2000/svg">
                                    <g transform={`translate(${2 + maxRadius}, ${2 + r})`}>
                                        <ellipse cx="0" cy="0" rx={r} ry={r} fill={color} />
                                    </g>
                                </svg>
                            </td>
                            <td style={{ textAlign: 'right' }}>
                                {value}
                                {tooltipUnit(units)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Typography>
    );
}

function roundValue(value, valueFormatType) {
    switch (valueFormatType) {
        case ValueFormatTypes.DECADE:
            return roundToNearestDecade(value);
        case ValueFormatTypes.WHOLE_NUMBER:
            return getFormattedValue(value, 0);
        case ValueFormatTypes.TENTHS:
            return getFormattedValue(value, 1);
        case ValueFormatTypes.HUNDREDTHS:
            return getFormattedValue(value, 2);
        case ValueFormatTypes.THOUSANDTHS:
            return getFormattedValue(value, 3);
        default:
            return value;
    }
}

function getDefaultValueFormatting(property) {
    const propInfo = screeningPropInfo(property);
    let format = ValueFormatTypes.TENTHS;
    switch (propInfo?.precision) {
        case 0:
            format = ValueFormatTypes.WHOLE_NUMBER;
            break;
        case 1:
            format = ValueFormatTypes.TENTHS;
            break;
        case 2:
            format = ValueFormatTypes.HUNDREDTHS;
            break;
        case 3:
            format = ValueFormatTypes.THOUSANDTHS;
            break;
        default:
            break;
    }
    if (propInfo?.defaultScale === ScaleTypes.LOGARITHMIC) {
        format = ValueFormatTypes.DECADE;
    }
    return format;
}

function calculateCoralMapStyles({
    activeProperty, activeScreeningCompoundInfoList, propertyPreference,
    availableScreeningCompoundInfoList, propertyLabel, propertySettings,
}) {
    if (!propertySettings || !activeProperty) {
        return {};
    }
    let absoluteMin = null;
    let absoluteMax = null;
    for (const screeningCompoundInfo of activeScreeningCompoundInfoList) {
        if (!screeningCompoundInfo) continue;
        const min = screeningCompoundInfo
            .getMinValue(activeProperty, propertySettings.propertyThresholds[0]);
        const max = screeningCompoundInfo
            .getMaxValue(activeProperty, propertySettings.propertyThresholds[1]);
        if ((absoluteMin == null || absoluteMin > min) && min != null) absoluteMin = min;
        if ((absoluteMax == null || absoluteMax < max) && max != null) absoluteMax = max;
    }
    const baseStyles = {
        branchColor: 'lightgray',
        label: { textColor: 'black' },
    };

    const styleProperties = {};
    for (const screeningCompoundInfo of activeScreeningCompoundInfoList) {
        if (!screeningCompoundInfo) continue;
        const h = screeningCompoundInfo.getBaseHue();
        const s = 1;
        const kinaseValues = screeningCompoundInfo.getKinaseValuesForProperty(activeProperty);
        for (const [kinase, propertyValue] of Object.entries(kinaseValues)) {
            if (propertyValue === null || propertyValue === undefined) continue;
            if (!styleProperties[kinase]) styleProperties[kinase] = {};
            if (styleProperties[kinase].tooltips) {
                styleProperties[kinase].tooltips.push({
                    name: getNumberedCompoundName(
                        screeningCompoundInfo,
                        availableScreeningCompoundInfoList
                    ),
                    smiles: screeningCompoundInfo.smiles,
                    property: propertyLabel,
                    value: propertyValue,
                });
            } else {
                styleProperties[kinase].tooltips = [
                    {
                        name: getNumberedCompoundName(
                            screeningCompoundInfo,
                            availableScreeningCompoundInfoList
                        ),
                        smiles: screeningCompoundInfo.smiles,
                        property: propertyLabel,
                        value: propertyValue,
                    },
                ];
            }
            if (propertyValue < propertySettings.propertyThresholds[0]
                || propertyValue > propertySettings.propertyThresholds[1]) continue;
            const useSignedLog = propertySettings.scale === ScaleTypes.LOGARITHMIC;
            const l = calculateInterpolatedValue(
                propertyValue, absoluteMin, absoluteMax,
                propertySettings.colorIntensity[1],
                propertySettings.colorIntensity[0],
                {
                    useSignedLog,
                    propertyPreference,
                }
            );
            const adjustedColor = hslToRgb(h, s, l);
            const nodeSize = calculateInterpolatedValue(
                propertyValue, absoluteMin, absoluteMax,
                propertySettings.nodeSizeValues[0],
                propertySettings.nodeSizeValues[1],
                {
                    useSignedLog,
                    propertyPreference,
                },
            );
            if (styleProperties[kinase].nodeSize) {
                const prevNodeSize = styleProperties[kinase].nodeSize;
                styleProperties[kinase].nodeSize = (prevNodeSize + nodeSize) / 2;
            } else {
                styleProperties[kinase].nodeSize = nodeSize;
            }
            if (styleProperties[kinase].color) {
                const prevBranchColor = styleProperties[kinase].color;
                const averagedColors = [];
                for (let i = 0; i < 3; i++) {
                    averagedColors[i] = (prevBranchColor[i] + adjustedColor[i]) / 2;
                }
                styleProperties[kinase].color = averagedColors;
            } else {
                styleProperties[kinase].color = adjustedColor;
            }
        }
    }
    const kinaseStyles = {};
    for (const [kinase, properties] of Object.entries(styleProperties)) {
        kinaseStyles[kinase] = {
            branchColor: !!properties.color && `rgb(${properties.color[0]},${properties.color[1]},${properties.color[2]})`,
            nodeColor: !!properties.color && `rgb(${properties.color[0]},${properties.color[1]},${properties.color[2]})`,
            nodeSize: !!properties.nodeSize && properties.nodeSize,
            tooltip: (
                <div>
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBotton: '.5rem',
                    }}
                    >
                        <span>{kinase}</span>
                        <span>{properties.tooltips[0].property}</span>

                    </div>
                    {properties.tooltips.map((tooltip) => (
                        <div key={tooltip.smiles} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
                            <span>{`${tooltip.name || tooltip.smiles}:`}</span>
                            <span>{getFormattedValue(tooltip.value, 3)}</span>
                        </div>
                    ))}
                </div>
            ),
        };
    }

    return {
        ...baseStyles,
        ...kinaseStyles,
    };
}

function calculateInterpolatedValue(
    propertyValue,
    minPropertyValue,
    maxPropertyValue,
    minOutputValue,
    maxOutputValue,
    {
        useSignedLog = false,
        propertyPreference = 'positive',
        clamp = true,
    } = {}
) {
    let x = propertyValue;
    let x0 = minPropertyValue;
    let x1 = maxPropertyValue;

    if (useSignedLog) {
        x = signedLog(x);
        x0 = signedLog(x0);
        x1 = signedLog(x1);
    }

    const lo = Math.min(x0, x1);
    const hi = Math.max(x0, x1);

    if (lo === hi) return maxOutputValue;

    // clamping values ensures that we don't get invalid output.
    // node size or lightness less than zero need to be avoided.
    if (clamp) {
        x = Math.min(Math.max(x, lo), hi);
    }

    // Normalize to 0..1
    let t = (x - lo) / (hi - lo);

    // swap based on preference
    if (propertyPreference === 'negative') {
        t = 1 - t;
    }

    return minOutputValue + t * (maxOutputValue - minOutputValue);
}

function signedLog(x, k = 0.0001) {
    if (x === 0) return 0;
    return Math.sign(x) * Math.log10(1 + Math.abs(x) / k);
}

function signedLogInverse(y, k = 0.0001) {
    if (y === 0) return 0;
    return Math.sign(y) * (k * (10 ** Math.abs(y) - 1));
}

/**
 *
 * @param {ScreeningCompoundInfo[]} screeningCompoundInfoList
 */
function getAllAvailableProperties(screeningCompoundInfoList) {
    const propertyList = [];
    for (const screeningCompoundInfo of screeningCompoundInfoList) {
        const properties = screeningCompoundInfo.getPropertyList();
        for (const property of properties) {
            if (!propertyList.includes(property)) propertyList.push(property);
        }
    }

    sortScreeningProperties(propertyList);
    return propertyList;
}

/**
 * Sort screening properties, giving priority to the order in JobDefinition
 * @param {Array} list
 * @param {Function?} transform
 */
export function sortScreeningProperties(list, transform=(a) => a) {
    const knownProps = Object.keys(lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo || {});
    list.sort((a, b) => {
        const aIndex = knownProps.indexOf(transform(a));
        const bIndex = knownProps.indexOf(transform(b));
        if (aIndex === -1 && bIndex === -1) return 0;
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
    });
    return list;
}

function getScreeningPropertySort(property) {
    const directionality = screeningPropInfo(property)?.directionality;
    if (!directionality) {
        const preferNegative = [];
        return preferNegative.includes(property) ? 'negative' : 'positive';
    }

    if (directionality === Directionality.MINIMIZE) {
        return 'negative';
    } else { // Fallback will include MAXIMIZE and N/A case
        return 'positive';
    }
}

function screeningPropInfo(propKey, propInfosIn) {
    const propInfos = propInfosIn || lookupJobDefinition(JobTypes.LIBRARY_SCREEN).dataInfo;
    return propInfos?.[propKey];
}

function roundToNearestDecade(value) {
    if (value === 0) return 0;
    const sign = Math.sign(value);
    const a = Math.abs(value);
    const exp = Math.round(Math.log10(a));
    return sign * (10 ** exp);
}

/**
 * Component that can handle either linear or logarithmic property sliders
 */
function FlexibleSlider({
    value: valueIn, onChange: onChangeIn, min: minIn, max: maxIn, step: stepIn,
    logarithmic,
    unit, precision=defaultPrecision,
    ...rest
}) {
    let value = valueIn;
    let min = minIn;
    let max = maxIn;
    let step = 10 ** -precision;
    let onChange = onChangeIn;
    let valueForDisplay = (v) => v;
    if (logarithmic) {
        onChange = (event, newValue) => {
            const newVal = Array.isArray(value)
                ? newValue.map((v) => signedLogInverse(v))
                : signedLogInverse(newValue);
            onChangeIn(event, newVal);
        };
        value = Array.isArray(value) ? value.map((v) => signedLog(v)) : signedLog(value);
        min = signedLog(min);
        max = signedLog(max);
        step = (max - min) / 100;
        valueForDisplay = (val) => signedLogInverse(val);
    }
    return (
        <Slider
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            valueLabelFormat={(v) => (
                `${getFormattedValue(valueForDisplay(v), precision)}${tooltipUnit(unit)}`
            )}
            {...rest}
        />
    );
}

/**
 * Get unit string for tooltip display, based on the units in JobDefinitions.
 * @param {string} unit
 * @returns {string}
 */
function tooltipUnit(unit) {
    switch (true) {
        case !unit: return '';
        case unit === 'Probability': return '';
        case unit === '(composite)': return '';
        default: return ` ${unit}`;
    }
}
